import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  RunnerModifierEnum,
  RunnerSortOrderEnum,
  RunnerSortByEnum
} from "../types";
import {
  gtmOnFilterApplied,
  gtmOnSortApplied
} from "../utils/gtm/runnerModifier";
import { openLoginWall } from "../utils/general";
import { setSelectedRunnerModifier } from "../store/actions";
import { getSelectedRunnerModifier } from "../store/selectors";
import { modifiersOptions } from "../components/Main/RunnerModifier/constants";

interface UseRunnerModifierProps {
  isLogged: boolean;
  trackName: string;
  raceNumber: string;
}

const useRunnerModifier = ({
  isLogged,
  trackName,
  raceNumber
}: UseRunnerModifierProps) => {
  const dispatch = useDispatch();
  const defaultRunnerModifier = useSelector(getSelectedRunnerModifier);

  const [runnerModifier, setRunnerModifier] = useState<RunnerModifierEnum>(
    defaultRunnerModifier || RunnerModifierEnum.DEFAULT
  );
  const [runnerSortOrder, setRunnerSortOrder] = useState<RunnerSortOrderEnum>(
    RunnerSortOrderEnum.ASC
  );
  const [runnerSortBy, setRunnerSortBy] = useState<RunnerSortByEnum>(
    RunnerSortByEnum.SADDLECLOTH
  );

  useEffect(() => {
    if (modifiersOptions.advanced.includes(runnerSortBy)) {
      setRunnerModifier(RunnerModifierEnum.ADVANCED);
    } else if (modifiersOptions.performance.includes(runnerSortBy)) {
      setRunnerModifier(RunnerModifierEnum.PERFORMANCE);
    }
  }, [runnerSortBy]);

  useEffect(() => {
    dispatch(setSelectedRunnerModifier(runnerModifier));
  }, [runnerModifier]);

  const onSortOrderChangeHandler = (sortOrder: RunnerSortOrderEnum) => {
    setRunnerSortOrder(sortOrder);
    gtmOnSortApplied({
      sortOperation: sortOrder,
      sortValue: runnerSortBy,
      trackName,
      raceNumber
    });
  };

  const onSortByChangeHandler = (sortBy: RunnerSortByEnum) => {
    if (isLogged || sortBy === RunnerSortByEnum.SADDLECLOTH) {
      setRunnerSortBy(sortBy);
    } else {
      openLoginWall(
        () => setRunnerSortBy(sortBy),
        () => setRunnerSortBy(RunnerSortByEnum.SADDLECLOTH)
      );
    }

    gtmOnSortApplied({
      sortOperation: runnerSortOrder,
      sortValue: sortBy,
      trackName,
      raceNumber
    });
  };

  const onRunnerModifierChangeHandler = (
    newRunnerModifier: RunnerModifierEnum
  ) => {
    if (isLogged || newRunnerModifier === RunnerModifierEnum.DEFAULT) {
      setRunnerModifier(newRunnerModifier);
    } else {
      openLoginWall(
        () => setRunnerModifier(newRunnerModifier),
        () => setRunnerModifier(RunnerModifierEnum.DEFAULT)
      );
    }

    gtmOnFilterApplied({
      filter: newRunnerModifier,
      trackName
    });
  };

  return {
    runnerModifier,
    runnerSortOrder,
    runnerSortBy,
    onRunnerModifierChangeHandler,
    onSortOrderChangeHandler,
    onSortByChangeHandler
  };
};

export default useRunnerModifier;
